/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { AuthWrapper, PrivateRoute } from 'modules/common/auth/components';
import {
  ADMIN_ROLE,
  PLATFORM_ADMIN_ROLE,
  PREFERRED_ADMIN_ROLE,
  SUPER_ADMIN_ROLE,
} from 'modules/common/constants/roles';
import ROUTES from 'modules/common/constants/route';
import AuthLayout from 'modules/common/layouts/auth';
import DashboardLayout from 'modules/common/layouts/dashboard';
import Page404 from 'modules/error-pages/404';
import { ForgotPasswordView } from 'modules/forgot-password/components';
import { ResetPasswordView } from 'modules/reset-password/components';
import { SignInView } from 'modules/sign-in/components';
import { UsersView } from 'modules/users/components';
import VerifyCallback from 'modules/verify-callback/components';
import { VisualFilterView } from 'modules/visual-filter/components';
import { HotelView } from 'modules/hotels/components';
import { VisualView } from 'modules/visual/components';
import { EventCategoriesConfigureView } from 'modules/event-configuration/components';
import { UserInvitationView } from 'modules/user-invitation/components';
import { Navigate } from 'react-router-dom';
import SignInLayout from 'modules/common/layouts/sign-in';
import SignInAuthWindow from 'modules/sign-in/components/auth-window';
import hotelSetView from 'modules/hotel-set/components/hotel-set-view';
import transactionView from 'modules/transactions/components/transaction-view';
import SystemView from 'modules/system/components/system-view';
import { ScenarioView } from 'modules/scenario/components';
import { PlatformAdminSettingsView } from 'modules/platform-admin-settings/components';
import { LlmView } from '../modules/llm/components';
import AuditLogsView from '../modules/audit-logs/components/audit-logs-view';

/**
 * Define the routing structure using array. Here include the nested routeing as well.
 * And also define the auth wrapper and private routers for helping to role based routes
 */
const routes = [
  {
    path: ROUTES.ROOT,
    element: <Navigate to={ROUTES.LOGIN} />,
  },
  {
    path: ROUTES.ROOT,
    element: <SignInLayout />,
    children: [
      {
        path: ROUTES.LOGIN,
        element: <SignInView />,
      },
      {
        path: ROUTES.LOGIN_REDIRECT,
        element: <SignInAuthWindow />,
      },
    ],
  },
  {
    path: ROUTES.ROOT,
    element: <AuthLayout />,
    children: [
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: <ForgotPasswordView />,
      },
      {
        path: ROUTES.RESET_PASSWORD,
        element: <ResetPasswordView />,
      },
    ],
  },
  {
    path: ROUTES.DASHBOARD,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute
            component={VisualView}
            roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.VISUAL_FILTER,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute component={VisualFilterView} roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE]} />
        ),
      },
    ],
  },
  {
    path: ROUTES.USERS,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute
            component={UsersView}
            roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.HOTEL_SET,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute
            component={hotelSetView}
            roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.EVENT_CONFIGURATION,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute
            component={EventCategoriesConfigureView}
            roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.USER_INVITATION,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute
            component={UserInvitationView}
            roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.AUDIT_LOGS,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: <PrivateRoute component={AuditLogsView} roles={[SUPER_ADMIN_ROLE]} />,
      },
    ],
  },
  {
    path: ROUTES.HOTELS,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: <PrivateRoute component={HotelView} roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE]} />,
      },
    ],
  },
  {
    path: ROUTES.TRANSACTIONS,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute
            component={transactionView}
            roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.SYSTEM,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute
            component={SystemView}
            roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.SCENARIO,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute
            component={ScenarioView}
            roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE]}
          />
        ),
      },
    ],
  },
   {
    path: ROUTES.LLM_STATISTICS,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute
            component={LlmView}
            roles={[SUPER_ADMIN_ROLE, ADMIN_ROLE, PREFERRED_ADMIN_ROLE]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.PLATFORM_ADMIN_SETTINGS,
    element: (
      <AuthWrapper>
        <DashboardLayout />
      </AuthWrapper>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute component={PlatformAdminSettingsView} roles={[PLATFORM_ADMIN_ROLE]} />
        ),
      },
    ],
  },
  {
    path: ROUTES.NOT_FOUND,
    element: <Page404 />,
  },
  {
    path: ROUTES.VERIFY_CALLBACK,
    element: <VerifyCallback />,
  },
];
//
export default routes;
